/**
 * RestoreConfirmDialog — modal asking the user to confirm a restore
 * to a specific version.
 *
 * The list endpoint already populates the target VersionRecord
 * (we pass it in via the `target` prop), so the dialog shows the
 * record's summary + actor + timestamp + a warning that explains
 * what the restore does. Pre-restore, the backend has not yet
 * computed `agents_affected` — that list is part of the restore
 * RESPONSE, so the dialog can't preview it. Post-restore, the
 * caller surfaces it via a toast.
 *
 * Built on the 2.0 AlertDialog primitive (portal, focus trap, Escape,
 * scroll lock, no backdrop-close). The Restore CTA is a plain Button2
 * (not AlertDialogAction) so the dialog stays open showing "Restoring…"
 * while the async runs — the parent owns close via `isOpen`.
 */
import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../../../../components/ui/AlertDialog';
import { VersionRecord } from '../../state/types';
import { OPERATION_META, agentDisplayName } from '../../utils/operationMeta';
import formatRelativeTime from '../../../../utils/formatRelativeTime';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import styles from './RestoreConfirmDialog.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX.
const ButtonAny = Button as unknown as React.ComponentType<any>;

interface RestoreConfirmDialogProps {
  isOpen: boolean;
  /** Target version the user wants to restore to. May be null when
   *  the dialog is animating closed and the pendingRestore state
   *  was cleared. The component renders nothing in that case. */
  target: VersionRecord | null;
  restoring: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function RestoreConfirmDialog({
  isOpen,
  target,
  restoring,
  onCancel,
  onConfirm,
}: RestoreConfirmDialogProps) {
  if (!isOpen || !target) return null;

  const meta = OPERATION_META[target.operation_type];
  const agentLabel = target.agent_name
    ? agentDisplayName(target.agent_name)
    : null;

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !restoring) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Restore to v{target.version_number}?</AlertDialogTitle>
        </AlertDialogHeader>

        <div className={styles.body}>
          <div className={styles.section}>
            <div className={styles.targetRow}>
              <span className={styles.versionId}>v{target.version_number}</span>
              <span className={styles.summary}>
                {meta.label}
                {agentLabel ? ` · ${agentLabel}` : ''} — {target.summary}
              </span>
            </div>
            <div className={styles.meta}>
              {target.user_display_name} ·{' '}
              {formatRelativeTime(target.created_at)}
            </div>
          </div>

          <div className={styles.warningBox}>
            <span className={styles.warningIcon} aria-hidden="true">
              <Icon name="warning" size={16} />
            </span>
            <span>
              A new restore entry will be appended at the top of the
              timeline. Existing versions are preserved — restores never
              delete history.
            </span>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
          <ButtonAny
            type="button"
            variant="default"
            onClick={onConfirm}
            loading={restoring}
            disabled={restoring}
          >
            {restoring ? 'Restoring…' : `Restore to v${target.version_number}`}
          </ButtonAny>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
